import React from "react";
import { Link } from "react-router-dom";
import { Github, Globe, GraduationCap, Home, Info, Linkedin, Shield, Twitter } from "lucide-react";
import { useTheme } from "../../context/ThemeContext";

export const Footer: React.FC = () => {
  const { resolvedTheme } = useTheme();

  return (
    <footer className="bg-slate-100 dark:bg-slate-950 border-t border-slate-200 dark:border-slate-900 text-slate-500 dark:text-slate-400 z-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-10">
          {/* Col 1 & 2: Brand Info */}
          <div className="lg:col-span-2 space-y-4">
            <Link to="/" className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-indigo-600/30 border border-indigo-500/40 flex items-center justify-center">
                <GraduationCap className="w-5 h-5 text-indigo-400" />
              </div>
              <div className="flex flex-col">
                <span className="text-xl font-bold text-slate-900 dark:text-white tracking-tight">Corvenio</span>
                <span className="text-[10px] text-slate-500 font-mono">v1.0.0 Enterprise Release</span>
              </div>
            </Link>
            <p className="text-xs text-slate-600 dark:text-slate-400 max-w-sm leading-relaxed">
              The next-generation multi-tenant SaaS platform empowering higher education institutions with real-time academic automation, finance management, dynamic RBAC, and AI advising.
            </p>
            <div className="flex items-center gap-3 pt-2">
              <a
                href="https://github.com/amaanmulani9-ai"
                target="_blank"
                rel="noreferrer"
                aria-label="GitHub Profile & Repository"
                className="w-9 h-9 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 flex items-center justify-center hover:bg-indigo-600 hover:text-white text-slate-600 dark:text-slate-300 transition-colors shadow-sm"
              >
                <Github className="w-4 h-4" />
              </a>
              <a
                href="https://twitter.com"
                target="_blank"
                rel="noreferrer"
                aria-label="Twitter Account"
                className="w-9 h-9 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 flex items-center justify-center hover:bg-indigo-600 hover:text-white text-slate-600 dark:text-slate-300 transition-colors shadow-sm"
              >
                <Twitter className="w-4 h-4" />
              </a>
              <a
                href="https://linkedin.com"
                target="_blank"
                rel="noreferrer"
                aria-label="LinkedIn Page"
                className="w-9 h-9 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 flex items-center justify-center hover:bg-indigo-600 hover:text-white text-slate-600 dark:text-slate-300 transition-colors shadow-sm"
              >
                <Linkedin className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Col 3: Product & Navigation */}
          <div className="space-y-3">
            <h4 className="text-xs font-bold text-slate-800 dark:text-slate-200 uppercase tracking-wider">Navigation</h4>
            <ul className="space-y-2 text-xs">
              <li>
                <Link to="/" className="hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">Home</Link>
              </li>
              <li>
                <Link to="/features" className="hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">Features</Link>
              </li>
              <li>
                <Link to="/modules" className="hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">ERP Modules</Link>
              </li>
              <li>
                <Link to="/about" className="hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">About Us</Link>
              </li>
            </ul>
          </div>

          {/* Col 4: Authentication */}
          <div className="space-y-3">
            <h4 className="text-xs font-bold text-slate-800 dark:text-slate-200 uppercase tracking-wider">Portals & Access</h4>
            <ul className="space-y-2 text-xs">
              <li>
                <Link to="/login" className="hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">Sign In</Link>
              </li>
              <li>
                <Link to="/register" className="hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">Create Institution</Link>
              </li>
              <li>
                <Link to="/login/student" className="hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">Student Portal</Link>
              </li>
              <li>
                <Link to="/login/faculty" className="hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">Faculty Portal</Link>
              </li>
            </ul>
          </div>

          {/* Col 5: Help & Support */}
          <div className="space-y-3">
            <h4 className="text-xs font-bold text-slate-800 dark:text-slate-200 uppercase tracking-wider">Support</h4>
            <ul className="space-y-2 text-xs">
              <li>
                <Link to="/contact" className="hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">Contact</Link>
              </li>
              <li>
                <Link to="/help" className="hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">Help Center</Link>
              </li>
            </ul>
          </div>

          {/* Col 6: Trust & Compliance */}
          <div className="space-y-3">
            <h4 className="text-xs font-bold text-slate-800 dark:text-slate-200 uppercase tracking-wider">Trust & Security</h4>
            <ul className="space-y-2 text-xs">
              <li className="flex items-center gap-1.5 text-slate-600 dark:text-slate-400">
                <Shield className="w-4 h-4 text-emerald-600 dark:text-emerald-400" /> Enterprise Security Architecture
              </li>
              <li className="flex items-center gap-1.5 text-slate-600 dark:text-slate-400">
                <Globe className="w-4 h-4 text-indigo-600 dark:text-indigo-400" /> Schema-Isolated SaaS
              </li>
              <li>
                <Link to="/privacy" className="hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">Privacy Policy</Link>
              </li>
              <li>
                <Link to="/terms" className="hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">Terms of Service</Link>
              </li>
              <li className="pt-1 text-[11px] text-slate-500">
                Theme: <span className="text-slate-800 dark:text-slate-300 font-semibold capitalize">{resolvedTheme}</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-slate-200 dark:border-slate-900 flex flex-col md:flex-row items-center justify-between text-xs text-slate-500 gap-4">
          <p>© {new Date().getFullYear()} Corvenio Enterprise College ERP. All rights reserved.</p>

          <div className="flex items-center gap-6">
            <Link to="/privacy" className="hover:text-slate-900 dark:hover:text-slate-300 transition-colors">Privacy</Link>
            <Link to="/terms" className="hover:text-slate-900 dark:hover:text-slate-300 transition-colors">Terms</Link>
            <Link to="/status" className="hover:text-slate-900 dark:hover:text-slate-300 transition-colors">Status</Link>
            <Link to="/contact" className="hover:text-slate-900 dark:hover:text-slate-300 transition-colors">Contact</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};
